import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { PlexEnvironment } from '@app/env';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { DisabledControlDirective } from '@app/shared/directives/disabled-control/disabled-control.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { CheckBoxDirective, NumericTextBoxComponent } from '@progress/kendo-angular-inputs';
import { ErrorService } from 'app/common/services/error.service';
import { ProjectStatusId } from 'app/shared/data/project-enums';
import { Subject } from 'rxjs';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectDrillingPhaseStepComponent } from '../project-drilling-phase-step/project-drilling-phase-step.component';
import { ProjectDrillingObject } from './models/project-drilling-object';
import { ProjectDrillingParametersFormationTop } from './models/project-drilling-parameters-formation-top';
import { ProjectDrillingParametersOverride } from './models/project-drilling-parameters-override';

interface CurveBuild {
  CurveBuildId: number;
  Description: string;
}

interface CuttingsDisposalMethod {
  CuttingsDisposalMethodId: number;
  Name: string;
}

enum ProjectParameters {
  SurfaceCasing = 0,
  Intermediate1 = 1,
  Intermediate2 = 2,
  Intermediate3 = 3,
  KickOffPoint = 4,
  LandingPoint = 5,
  LateralLength = 6,
  TotalDepth = 7
}

@Component({
  selector: 'app-project-drilling-details',
  templateUrl: './project-drilling-details.component.html',
  styleUrls: ['./project-drilling-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    ButtonLoadingDirective,
    CheckBoxDirective,
    ComboBoxComponent,
    DisabledControlDirective,
    FormsModule,
    NumericTextBoxComponent,
    ProjectDrillingPhaseStepComponent,
    ReactiveFormsModule,
  ],
})
export class ProjectDrillingDetailsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  public icons = kendoIcons;

  readonly xProjectId = input<number>(undefined);
  readonly xProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  // emitter tabTdv provides touched-dirty-valid status of the form in this tab
  readonly tabTdv = output<boolean>();
  readonly saveComplete = output<boolean>();

  get formationTopValues(): UntypedFormArray {
    return this.drillParameterForm.get('formationTopValues') as UntypedFormArray;
  }

  get overrideValues(): UntypedFormArray {
    return this.drillParameterForm.get('overrideValues') as UntypedFormArray;
  }

  public drillObject: ProjectDrillingObject;
  public cloneDrillObject: ProjectDrillingObject;
  public dFormationTops = new Array<ProjectDrillingParametersFormationTop>();
  public dOverrides = new Array<ProjectDrillingParametersOverride>();
  public dCurveBuild = new Array<CurveBuild>();
  public dCuttingsDisposalMethod = new Array<CuttingsDisposalMethod>();
  public hasSisterPad: boolean;
  public hasSpudderRig: boolean;
  public offLeasePop: boolean;
  public targetFormation: string;
  public projectAssetTeamId: number;
  public tempStepOutOverride: number;
  public tempStepOut: number;
  public isStepOut: boolean;
  public tempLateralLengthOverride: number;
  public isLateralLength: boolean;
  public tempLateralLength: number;
  public tempSurfaceCasingOverride: number;
  public tempSurfaceCasing: number;
  public isSurfaceCasing: boolean;
  public tempIntermediateOneOverride: number;
  public tempIntermediateOne: number;
  public isIntermediateOne: boolean;
  public tempLandingPointOverride: number;
  public tempLandingPoint: number;
  public istempLandingPoint: boolean;
  public tempIntermediateTwoOverride: number;
  public tempIntermediateTwo: number;
  public isIntermediateTwo: boolean;
  public tempIntermediateThreeOverride: number;
  public tempIntermediateThree: number;
  public tempWellsInDrillGroup: number;
  public wellsInDrillGroup: number;
  public wellsOnPad: number;
  public padSize: number;
  public tempHasSpudderRig: boolean;
  public tempKickOffPointMeasuredDepthOverride: number;
  public tempKickOffPointMeasuredDepth: number;
  public isKickOffPointMeasuredDepthOverride: boolean;
  public tempWellTotalDepthMeasuredDepthOverride: number;
  public tempWellTotalDepthMeasuredDepth: number;
  public isWellTotalDepthMeasuredDepthOverride: boolean;
  public decimalPrecision: number = 0;
  public decimalFormat: string = 'n0';
  public decimalFormat3: string = 'n3';
  public decimalPrecision3: number = 3;
  public decimalFormat2: string = 'n2';
  public decimalPrecision2: number = 2;
  public percentFormat: string = 'p0';
  public drillScheduleButtonLabel: string = 'Toggle Drill Schedule';
  public drillParameterForm: UntypedFormGroup;
  public isFormReady: boolean;
  public padExists: boolean;
  public errorMessage: string;
  public lpkbTVD: number;
  public showDataErrorMessage: boolean;
  public dataErrorMessage: string = 'errors have occurred';
  public showErrorScenarioWellId: boolean;
  public showErrorTargetFormationId: boolean;
  public showErrorPadId: boolean;
  public showErrorRigId: boolean;
  public errorScenarioWellId: string = 'Project Well is missing a ScenarioWellId';
  public errorTargetFormationId: string = 'Project Well is missing a TargetFormationId';
  public errorPadId: string = 'Project Well is missing a PadId';
  public errorRigId: string = 'Project Well is missing a RigId';
  public disableAllEdits: boolean;
  public aScatterLine: any[] = [];
  public loading: boolean = false;

  private putUrl: string = 'estimation/projects/processdrillingparameters';
  private tCurveBuild: string = 'CurveBuilds';
  private oCurveBuild: string = '?$select=CurveBuildId%2C%20Description';
  private tCuttingsDisposalMethod: string = 'CuttingsDisposalMethod';
  private oCuttingsDisposalMethod: string = '?$select=CuttingsDisposalMethodId%2C%20Name';
  public refreshPhaseStepDetail: Subject<boolean> = new Subject();

  constructor() {
    this.generateForm();
  }

  ngOnInit() {
    this.showDataErrorMessage = false;
    let url = `estimation/projects`;
    let endpoint = `${this.xProjectId()}/drillingparameters?useNewEngine=${this.environment.useNewEngine}`;
    this.getCurveBuilds(this.tCurveBuild, this.oCurveBuild);
    this.getCuttingsDisposalMethods(this.tCuttingsDisposalMethod, this.oCuttingsDisposalMethod);
    this.getAssetTeamByProjectId(this.xProjectId());

    if (this.iProjectStatusId() === ProjectStatusId.Closed || this.iProjectStatusId() === ProjectStatusId.Cancelled) {
      this.disableAllEdits = true;
      this.drillParameterForm.disable();
    } else {
      this.disableAllEdits = false;
    }

    this.getDrillDetails(url, endpoint);
  }

  private getCurveBuilds(table: string, oQuery: string): void {
    this.lookupPlusService.getLookup(table, oQuery, true)
      .then(payload => {
        this.dCurveBuild = payload.value;
      });
  }

  private getCuttingsDisposalMethods(table: string, oQuery: string): void {
    this.lookupPlusService.getLookup(table, oQuery, true)
      .then(payload => {
        this.dCuttingsDisposalMethod = payload.value;
      });
  }

  private getAssetTeamByProjectId(id: number): void {
    let url: string = `Projects(${id})?$select=AssetTeamId`;
    this.lookupPlusService.getSingleTableRow(url)
      .then(payload => {
        this.projectAssetTeamId = payload.AssetTeamId;
      });
  }

  private getDrillingDataItem(key: string): number {
    let selected: any = this.drillObject.DrillingData.find(x => x.Key === key);
    return selected !== undefined ? selected.Value : 0;
  }

  private getCurveBuildSelectorItem(value: string): CurveBuild {
    let selected: any = this.dCurveBuild.find(x => x.Description === value);
    return selected !== undefined ? selected : null;
  }

  private getCuttingsDisposalMethodSelectorItem(value: number): CuttingsDisposalMethod {
    let selected: any;
    if (value != null && value > 0)
      selected = this.dCuttingsDisposalMethod.find(x => x.CuttingsDisposalMethodId === value);
    else {
      selected = this.dCuttingsDisposalMethod.find(x => x.CuttingsDisposalMethodId === 1);
    }
    return selected !== undefined ? selected : null;
  }

  private async getDrillDetails(command: string, endpoint: any): Promise<void> {
    this.dFormationTops = [];
    this.dOverrides = [];
    this.loading = true;

    try {
      let payload = await this.lookupPlusService.getObject(command, endpoint);

      this.drillObject = payload;
      this.cloneDrillObject = Object.assign({}, payload);
      // general overrides
      if (payload.IsIntermediateWaterBasedMud == null)
        this.drillParameterForm.get('IsIntermediateWaterBasedMud').disable();
      else
        this.drillParameterForm.get('IsIntermediateWaterBasedMud').enable();
      if (payload.IsIntermediateThreeWaterBasedMud == null)
        this.drillParameterForm.get('IsIntermediateThreeWaterBasedMud').disable();
      else
        this.drillParameterForm.get('IsIntermediateThreeWaterBasedMud').enable();
      this.targetFormation = payload.TargetFormation;
      this.hasSisterPad = payload.HasSisterPad;
      // create temporary versions of the override values for use with override selectors
      this.tempLateralLengthOverride = payload.LateralLengthOverride;
      this.tempSurfaceCasingOverride = payload.SurfaceCasingDepthMeasuredDepthOverride;
      this.tempIntermediateOneOverride = payload.IntermediateOneCasingDepthMeasuredDepthOverride;
      this.tempIntermediateTwoOverride = payload.IntermediateTwoCasingDepthMeasuredDepthOverride;
      this.tempIntermediateThreeOverride = payload.IntermediateThreeCasingDepthMeasuredDepthOverride;
      this.tempKickOffPointMeasuredDepthOverride = payload.KickOffPointMeasuredDepthOverride;
      this.tempWellTotalDepthMeasuredDepthOverride = payload.WellTotalDepthMeasuredDepthOverride;
      this.tempStepOutOverride = payload.StepOutOverride;
      this.padExists = payload.PadExists;
      this.hasSpudderRig = null;
      this.wellsOnPad = this.drillObject.WellsOnPad;
      this.wellsInDrillGroup = this.drillObject.WellsInDrillGroup;

      this.tempLateralLength = payload.LateralLength;
      this.tempSurfaceCasing = payload.SurfaceCasingDepthMeasuredDepth;
      this.tempIntermediateOne = payload.IntermediateOneCasingDepthMeasuredDepth;
      this.tempIntermediateTwo = payload.IntermediateTwoCasingDepthMeasuredDepth;
      this.tempIntermediateThree = payload.IntermediateThreeCasingDepthMeasuredDepth;

      this.tempLandingPoint = payload.LandingPointMeasuredDepth;
      this.tempLandingPointOverride = payload.LandingPointOverride;

      this.wellsInDrillGroup = payload.WellsInDrillGroup;

      this.tempKickOffPointMeasuredDepth = payload.KickOffPointMeasuredDepth;
      this.tempWellTotalDepthMeasuredDepth = payload.WellTotalDepthMeasuredDepth;
      this.tempStepOut = payload.StepOut;

      if (this.drillObject.DrillingData) {
        if (this.drillObject.DrillingData.length > 0) {
          this.padSize = this.getDrillingDataItem('PAD_SIZE');
        }
      }

      if (this.drillObject.FormationTopsData) {
        if (this.drillObject.FormationTopsData.length > 0) {
          this.drillObject.FormationTopsData.forEach(element => {
            this.dFormationTops.push(element);
          });
        }
      }

      this.dOverrides.push(this.addOverrideArrayElement('Surface Casing Measured Depth', this.tempSurfaceCasing, this.tempSurfaceCasingOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Intermediate Casing Measured Depth', this.tempIntermediateOne, this.tempIntermediateOneOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Intermediate 2 Casing Measured Depth', this.tempIntermediateTwo, this.tempIntermediateTwoOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Intermediate 3 Casing Measured Depth', this.tempIntermediateThree, this.tempIntermediateThreeOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Kick-Off Point Measured Depth', this.tempKickOffPointMeasuredDepth, this.tempKickOffPointMeasuredDepthOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Landing Point MD', this.tempLandingPoint, this.tempLandingPointOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Derived Drilled Lateral Length', this.tempLateralLength, this.tempLateralLengthOverride));
      this.dOverrides.push(this.addOverrideArrayElement('Total Depth Measured Depth', this.tempWellTotalDepthMeasuredDepth, this.tempWellTotalDepthMeasuredDepthOverride));

      this.populateForm();
      this.isFormReady = false;
    }
    catch (response) {
      this.errorService.error('Error getting Drill Details', response);
      this.dataErrorMessage = (response.error && response.error.Message) || response.message || response._body;
      this.showDataErrorMessage = true;
    }
    finally {
      this.loading = false;
    }
  }

  private populateForm(): void {
    this.drillParameterForm.reset();
    let curveBuild: CurveBuild;
    curveBuild = {
      CurveBuildId: 1,
      Description: `10 degrees/100 ft`
    };

    this.drillParameterForm.patchValue({
      CurveBuild: this.getCurveBuildSelectorItem(this.drillObject.CurveBuildDescription),
      CuttingsDisposalMethod: this.getCuttingsDisposalMethodSelectorItem(this.drillObject.CuttingsDisposalMethodId),
      HasSisterPad: this.drillObject.HasSisterPad,
      HasSpudderRig: this.drillObject.HasSpudderRig,
      IsMudLogging: this.drillObject.IsMudLogging,
      IsBelowGradeWellheadCellars: this.drillObject.IsBelowGradeWellheadCellars,
      IsRotarySteerable: this.drillObject.IsRotarySteerable,
      PadExists: this.padExists,
      IsIntermediateOilBasedMud: this.drillObject.IsIntermediateOilBasedMud,
      IsIntermediateWaterBasedMud: this.drillObject.IsIntermediateWaterBasedMud,
      IsIntermediateThreeWaterBasedMud: this.drillObject.IsIntermediateThreeWaterBasedMud,
      BottomHoleLocationXCoordinate: this.drillObject.BottomHoleLocationXCoordinate,
      BottomHoleLocationYCoordinate: this.drillObject.BottomHoleLocationYCoordinate,
      FirstTakePointXCoordinate: this.drillObject.FirstTakePointXCoordinate,
      FirstTakePointYCoordinate: this.drillObject.FirstTakePointYCoordinate,
      SurfaceHoleLocationPointOfPenetrationXCoordinate: this.drillObject.SurfaceHoleLocationPointOfPenetrationXCoordinate,
      SurfaceHoleLocationPointOfPenetrationYCoordinate: this.drillObject.SurfaceHoleLocationPointOfPenetrationYCoordinate,
      BottomHoleLocationXCoordinateOverride: this.drillObject.BottomHoleLocationXCoordinateOverride,
      BottomHoleLocationYCoordinateOverride: this.drillObject.BottomHoleLocationYCoordinateOverride,
      FirstTakePointXCoordinateOverride: this.drillObject.FirstTakePointXCoordinateOverride,
      FirstTakePointYCoordinateOverride: this.drillObject.FirstTakePointYCoordinateOverride,
      SurfaceHoleLocationPointOfPenetrationXCoordinateOverride: this.drillObject.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride,
      SurfaceHoleLocationPointOfPenetrationYCoordinateOverride: this.drillObject.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride,
      FormationTopOverride: this.drillObject.FormationTopOverride,
      LandingPointOverride: this.drillObject.LandingPointOverride,
      KickOffPointMeasuredDepthOverride: this.drillObject.KickOffPointMeasuredDepthOverride,
      WellTotalDepthMeasuredDepthOverride: this.drillObject.WellTotalDepthMeasuredDepthOverride
    });

    this.setFormationTopRows(this.dFormationTops);
    this.setOverrideRows(this.dOverrides);
  }

  private generateForm(): void {
    this.drillParameterForm = this.fb.group({
      CurveBuild: [null],
      CuttingsDisposalMethod: [null],
      HasSisterPad: [null],
      IsBelowGradeWellheadCellars: [null],
      IsMudLogging: [null],
      IsRotarySteerable: [null],
      PadExists: [null],
      IsIntermediateOilBasedMud: [null],
      IsIntermediateWaterBasedMud: [null],
      IsIntermediateThreeWaterBasedMud: [null],
      HasSpudderRig: [null],
      FormationTopOverride: [null],
      LandingPointOverride: [null],
      BottomHoleLocationXCoordinate: [null],
      BottomHoleLocationYCoordinate: [null],
      FirstTakePointXCoordinate: [null],
      FirstTakePointYCoordinate: [null],
      SurfaceHoleLocationPointOfPenetrationXCoordinate: [null],
      SurfaceHoleLocationPointOfPenetrationYCoordinate: [null],
      BottomHoleLocationXCoordinateOverride: [null],
      BottomHoleLocationYCoordinateOverride: [null],
      FirstTakePointXCoordinateOverride: [null],
      FirstTakePointYCoordinateOverride: [null],
      SurfaceHoleLocationPointOfPenetrationXCoordinateOverride: [null],
      SurfaceHoleLocationPointOfPenetrationYCoordinateOverride: [null],
      formationTopValues: this.fb.array([]),
      overrideValues: this.fb.array([])
    });
    this.drillParameterForm.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    let isValid: boolean = false;

    if (!this.drillParameterForm) {
      return;
    }

    if (this.drillParameterForm.valid && this.drillParameterForm.dirty) {
      isValid = true;
      this.isFormReady = true;
      this.setTDV();
    } else {
      isValid = false;
      this.isFormReady = false;
      this.clearTDV();
    }
  }

  private setFormationTopRows(rows: FormationTopRow[]): void {
    const ftFormGroups = rows.map(formationTop => this.fb.group(formationTop));
    const formationTopFormArray = this.fb.array(ftFormGroups);
    this.drillParameterForm.setControl('formationTopValues', formationTopFormArray);
  }

  private setOverrideRows(rows: OverrideRow[]): void {
    const ovFormGroups = rows.map(override => this.fb.group(override));
    const overrideFormArray = this.fb.array(ovFormGroups);
    this.drillParameterForm.setControl('overrideValues', overrideFormArray);
  }

  public cancelChanges(e): void {
    let url = `estimation/projects`;
    let endpoint = `${this.xProjectId()}/drillingparameters?useNewEngine=${this.environment.useNewEngine}`;
    this.isFormReady = false;
    this.getDrillDetails(url, endpoint);
  }

  public refreshParameters(useNewEngine: boolean): void {
    let url = `estimation/projects`;
    let endpoint = `${this.xProjectId()}/drillingparameters?refresh=true&useNewEngine=${useNewEngine}`;
    this.isFormReady = false;
    this.getDrillDetails(url, endpoint);
  }

  public saveParameters(e): void {
    this.prepareSave();
  }

  public prepareSave() {
    this.cloneDrillObject.HasSisterPad = this.drillParameterForm.controls.HasSisterPad.value;
    this.cloneDrillObject.IsBelowGradeWellheadCellars = this.drillParameterForm.controls.IsBelowGradeWellheadCellars.value;
    this.cloneDrillObject.HasSpudderRig = this.drillParameterForm.controls.HasSpudderRig.value;
    this.cloneDrillObject.IsMudLogging = this.drillParameterForm.controls.IsMudLogging.value;
    this.cloneDrillObject.PadExists = this.drillParameterForm.controls.PadExists.value;
    this.cloneDrillObject.IsRotarySteerable = this.drillParameterForm.controls.IsRotarySteerable.value;
    this.cloneDrillObject.IsIntermediateOilBasedMud = this.drillParameterForm.controls.IsIntermediateOilBasedMud.value;
    this.cloneDrillObject.IsIntermediateWaterBasedMud = this.drillParameterForm.controls.IsIntermediateWaterBasedMud.value;
    this.cloneDrillObject.IsIntermediateThreeWaterBasedMud = this.drillParameterForm.controls.IsIntermediateThreeWaterBasedMud.value;

    this.cloneDrillObject.FirstTakePointXCoordinate = this.drillParameterForm.controls.FirstTakePointXCoordinate.value,
    this.cloneDrillObject.FirstTakePointYCoordinate = this.drillParameterForm.controls.FirstTakePointYCoordinate.value,
    this.cloneDrillObject.BottomHoleLocationXCoordinate = this.drillParameterForm.controls.BottomHoleLocationXCoordinate.value,
    this.cloneDrillObject.BottomHoleLocationYCoordinate = this.drillParameterForm.controls.BottomHoleLocationYCoordinate.value,
    this.cloneDrillObject.SurfaceHoleLocationPointOfPenetrationXCoordinate = this.drillParameterForm.controls.SurfaceHoleLocationPointOfPenetrationXCoordinate.value;
    this.cloneDrillObject.SurfaceHoleLocationPointOfPenetrationYCoordinate = this.drillParameterForm.controls.SurfaceHoleLocationPointOfPenetrationYCoordinate.value;

    this.cloneDrillObject.FirstTakePointXCoordinateOverride = this.drillParameterForm.controls.FirstTakePointXCoordinateOverride.value,
    this.cloneDrillObject.FirstTakePointYCoordinateOverride = this.drillParameterForm.controls.FirstTakePointYCoordinateOverride.value,
    this.cloneDrillObject.BottomHoleLocationXCoordinateOverride = this.drillParameterForm.controls.BottomHoleLocationXCoordinateOverride.value,
    this.cloneDrillObject.BottomHoleLocationYCoordinateOverride = this.drillParameterForm.controls.BottomHoleLocationYCoordinateOverride.value,
    this.cloneDrillObject.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride = this.drillParameterForm.controls.SurfaceHoleLocationPointOfPenetrationXCoordinateOverride.value;
    this.cloneDrillObject.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride = this.drillParameterForm.controls.SurfaceHoleLocationPointOfPenetrationYCoordinateOverride.value;

    this.cloneDrillObject.FormationTopOverride = this.drillParameterForm.controls.FormationTopOverride.value;
    this.cloneDrillObject.CurveBuildDescription = this.drillParameterForm.controls.CurveBuild.value && this.drillParameterForm.controls.CurveBuild.value.Description;
    this.cloneDrillObject.CurveBuildId = this.drillParameterForm.controls.CurveBuild.value && this.drillParameterForm.controls.CurveBuild.value.CurveBuildId;
    this.cloneDrillObject.CuttingsDisposalMethodName = this.drillParameterForm.controls.CuttingsDisposalMethod.value && this.drillParameterForm.controls.CuttingsDisposalMethod.value.Name;
    this.cloneDrillObject.CuttingsDisposalMethodId = this.drillParameterForm.controls.CuttingsDisposalMethod.value && this.drillParameterForm.controls.CuttingsDisposalMethod.value.CuttingsDisposalMethodId;
    this.cloneDrillObject.FormationTopsData = this.drillParameterForm.controls.formationTopValues.value;

    this.cloneDrillObject.LateralLengthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.LateralLength].OverrideValue;
    this.cloneDrillObject.SurfaceCasingDepthMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.SurfaceCasing].OverrideValue;
    this.cloneDrillObject.IntermediateOneCasingDepthMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.Intermediate1].OverrideValue;
    this.cloneDrillObject.IntermediateTwoCasingDepthMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.Intermediate2].OverrideValue;
    this.cloneDrillObject.IntermediateThreeCasingDepthMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.Intermediate3].OverrideValue;
    this.cloneDrillObject.KickOffPointMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.KickOffPoint].OverrideValue;
    this.cloneDrillObject.LandingPointOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.LandingPoint].OverrideValue;
    this.cloneDrillObject.WellTotalDepthMeasuredDepthOverride = this.drillParameterForm.controls.overrideValues.value[ProjectParameters.TotalDepth].OverrideValue;

    this.projectCustomEndpointService.processDrilling(this.putUrl, this.cloneDrillObject)
      .then(response => {
        let url = `estimation/projects`;
        let endpoint = `${this.xProjectId()}/drillingparameters?useNewEngine=${this.environment.useNewEngine}`;
        this.getDrillDetails(url, endpoint);

        this.refreshPhaseStepDetail.next(true);

        this.saveComplete.emit(true);
      });
  }

  private addOverrideArrayElement(desc: string, value: number, override: number): ProjectDrillingParametersOverride {
    let arrayElement: ProjectDrillingParametersOverride = {
      Description: desc,
      Value: value,
      OverrideValue: override
    };
    return arrayElement;
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }
}

class FormationTopRow {
  ProjectDrillingParametersFormationTopId = null;
  FormationTopId = null;
  TargetFormationId = null;
  Description = '';
  KBTVD = null;
  OverrideValue = null;
}

class OverrideRow {
  Description = '';
  Value = null;
  OverrideValue = null;
}